import React from 'react';
import { CertType, CertStatus, IndFormData, EntFormData } from './types';

interface CertStatusResultProps {
  activeCert: CertType;
  individualStatus: CertStatus;
  enterpriseStatus: CertStatus;
  indForm: IndFormData;
  entForm: EntFormData;
  submittedAt?: string;
  auditedAt?: string;
}

export const getIdTypeLabel = (type?: string): string => {
  if (!type) return '居民身份证';
  switch (type.toUpperCase()) {
    case 'ID_CARD':
      return '居民身份证';
    case 'HK_RESIDENCE_PERMIT':
      return '港澳居民居住证';
    case 'MACAU_RESIDENCE_PERMIT':
      return '澳门居民居住证';
    case 'TAIWAN_RESIDENCE_PERMIT':
      return '台湾居民居住证';
    case 'HK_MACAU_TAIWAN_RESIDENCE_PERMIT':
      return '港澳台居民居住证';
    case 'FOREIGN_PERMANENT_RESIDENCE':
      return '外国人永久居留身份证';
    case 'HK_MACAU_HOME_RETURN_PERMIT':
      return '港澳居民来往内地通行证(回乡证)';
    case 'TAIWAN_COMPATRIOT_PERMIT':
      return '台湾居民来往大陆通行证(台胞证)';
    default:
      return type;
  }
};

export const maskIdNumber = (idNo?: string): string => {
  if (!idNo) return '-';
  if (idNo.length >= 15) {
    return idNo.slice(0, 4) + '**********' + idNo.slice(-4);
  }
  if (idNo.length > 6) {
    return idNo.slice(0, 3) + '****' + idNo.slice(-3);
  }
  return idNo;
};

export const maskPhone = (phone?: string): string => {
  if (!phone) return '-';
  if (phone.length === 11) {
    return phone.slice(0, 3) + '****' + phone.slice(-4);
  }
  return phone;
};

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return dateStr;
  }
};

export const CertStatusResult: React.FC<CertStatusResultProps> = ({
  activeCert,
  individualStatus,
  enterpriseStatus,
  indForm,
  entForm,
  submittedAt,
  auditedAt,
}) => {
  if (activeCert === 'none') return null;

  const currentStatus = activeCert === 'individual' ? individualStatus : enterpriseStatus;
  const isIndividual = activeCert === 'individual';

  return (
    <div className="space-y-6">
      {/* 审核中状态卡片 */}
      {currentStatus === 'pending' && (
        <div className="p-6 rounded-2xl bg-accent-50/70 border border-accent-200/80 space-y-5">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-accent-100 text-accent-600 flex items-center justify-center shrink-0 animate-pulse">
              <i className="ri-time-line text-2xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <h4 className="font-semibold text-base text-foreground-950">认证申请审核中</h4>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  审核中
                </span>
              </div>
              <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
                您的{isIndividual ? '个人实名认证' : '企业资质认证与入驻'}申请已提交成功，系统及运维团队正在处理中，预计 1-3 个工作日内完成审核。
              </p>
            </div>
          </div>

          {/* 提交的明细摘要 */}
          <div className="p-4 rounded-xl bg-white border border-accent-100 space-y-3 text-xs text-foreground-700">
            <div className="font-medium text-foreground-900 border-b border-foreground-100 pb-2 flex items-center justify-between">
              <span>申请提交明细</span>
              {submittedAt && <span className="text-[11px] text-foreground-400 font-normal">提交时间：{formatDate(submittedAt)}</span>}
            </div>

            {isIndividual ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-foreground-600">
                <div>
                  <span className="text-foreground-500">真实姓名：</span>
                  <span className="font-semibold text-foreground-900">{indForm.realName || '-'}</span>
                </div>
                <div>
                  <span className="text-foreground-500">证件类型：</span>
                  <span>{getIdTypeLabel(indForm.idType)}</span>
                </div>
                <div>
                  <span className="text-foreground-500">证件号码：</span>
                  <span className="font-mono">{maskIdNumber(indForm.idNumber)}</span>
                </div>
                <div>
                  <span className="text-foreground-500">联系电话：</span>
                  <span>{maskPhone(indForm.contactPhone || indForm.contactName)}</span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-foreground-600">
                <div>
                  <span className="text-foreground-500">企业全称：</span>
                  <span className="font-semibold text-foreground-900">{entForm.companyName || '-'}</span>
                </div>
                <div>
                  <span className="text-foreground-500">社会信用代码：</span>
                  <span className="font-mono">{entForm.creditCode || '-'}</span>
                </div>
                <div>
                  <span className="text-foreground-500">法定代表人：</span>
                  <span>{entForm.legalPerson || '-'}</span>
                </div>
                <div>
                  <span className="text-foreground-500">运营经办人：</span>
                  <span>
                    {entForm.contactName} {entForm.contactPhone ? `(${maskPhone(entForm.contactPhone)})` : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 认证通过卡片 */}
      {currentStatus === 'approved' && (
        <div className="p-6 rounded-2xl bg-green-50/60 border border-green-200/80 space-y-5">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-2xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <h4 className="font-semibold text-base text-foreground-950">
                  {isIndividual ? '个人实名认证已通过' : '企业资质认证已通过'}
                </h4>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                  已认证
                </span>
              </div>
              <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
                您的身份信息已通过官方权威校验，相关合规权益及特权已全量解锁，认证信息将严格加密保管。
              </p>
            </div>
          </div>

          {/* 认证明细表格卡片 */}
          <div className="p-4 rounded-xl bg-white border border-green-100 shadow-xs space-y-4">
            <div className="font-medium text-xs text-foreground-900 border-b border-foreground-100 pb-2.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <i className="ri-verified-badge-line text-green-600 text-sm"></i>
                已核验认证数据
              </span>
              {(auditedAt || submittedAt) && (
                <span className="text-[11px] text-foreground-400 font-normal">
                  认证时间：{formatDate(auditedAt || submittedAt)}
                </span>
              )}
            </div>

            {isIndividual ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs">
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">真实姓名</span>
                  <span className="font-semibold text-foreground-950">{indForm.realName || '-'}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">证件类型</span>
                  <span className="text-foreground-800">{getIdTypeLabel(indForm.idType)}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">证件号码</span>
                  <span className="font-mono font-medium text-foreground-900">{maskIdNumber(indForm.idNumber)}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">联系人及电话</span>
                  <span className="text-foreground-800">
                    {indForm.contactName || indForm.realName} ({maskPhone(indForm.contactPhone)})
                  </span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs">
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50 sm:col-span-2">
                  <span className="text-foreground-500">企业全称</span>
                  <span className="font-semibold text-foreground-950">{entForm.companyName || '-'}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">统一社会信用代码</span>
                  <span className="font-mono font-medium text-foreground-900">{entForm.creditCode || '-'}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50">
                  <span className="text-foreground-500">法定代表人</span>
                  <span className="text-foreground-800">
                    {entForm.legalPerson || '-'} ({getIdTypeLabel(entForm.legalRepresentativeIdType)})
                  </span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-foreground-50/50 sm:col-span-2">
                  <span className="text-foreground-500">运营经办人</span>
                  <span className="text-foreground-800">
                    {entForm.contactName || '-'} {entForm.contactPhone ? `(${maskPhone(entForm.contactPhone)})` : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertStatusResult;
